/**
 * Form utils
 *
 * Leverages JQuery where possible because JQ is already loaded by Webflow and accounts for browser
 * compatibility where possible
 */
type MaybeJQueryHtmlElement<ElementType extends HTMLElement> = ElementType | JQuery<ElementType>;

/**
 * Given an HTMLElement or an already JQuery wrapped element, return a JQuery wrapped element
 *
 */
function ensureJQueryObject<ElementType extends HTMLElement>(
  element: MaybeJQueryHtmlElement<ElementType>
) {
  return $(element);
}

/**
 * setSelectValue
 *
 * sets a select value, ensuring that the given option is valid.
 * Allows a fallback value in case the desired value is not found
 */
export function setSelectValue(
  select: MaybeJQueryHtmlElement<HTMLSelectElement>,
  value: string,
  fallbackValue?: string
) {
  // ensure we're working with a jQuery obj
  const $select = ensureJQueryObject(select);
  const $selectOptions = $select.children("option");
  const $firstOption = $selectOptions.first();

  const $selectOptionsLowerCase = $selectOptions
    .map((_idx, element) => element.value.toLocaleLowerCase())
    .toArray();

  for (const val of [value, fallbackValue]) {
    if (!val || typeof val !== "string") return;

    const $foundOptionIdx = $.inArray(val.toLocaleLowerCase(), $selectOptionsLowerCase);

    if ($foundOptionIdx !== -1) {
      const $foundOption = $selectOptions[$foundOptionIdx];

      if ($foundOption) {
        $select.val($foundOption.value);
        return;
      }
    }
  }

  // if we haven't found a value or the fallback, then we'll ensure that the
  // first option is set
  if ($firstOption[0]) {
    const firstOptionValue = $firstOption.val();

    if (typeof firstOptionValue === "string") {
      $select.val(firstOptionValue);
    }
  }
}

const DEFAULT_ERROR_STYLES: Record<string, number | string> = {
  color: "red",
};

export function addErrorTextAfterElement(
  inputEl: MaybeJQueryHtmlElement<HTMLElement>,
  msg: string,
  styles: Record<string, string> = {},
  errorElementTagName = "div"
) {
  const $el = ensureJQueryObject(inputEl);
  const $errorEl = $(`<${errorElementTagName}>${msg}</${errorElementTagName}>`);

  $errorEl.css({
    ...DEFAULT_ERROR_STYLES,
    ...styles,
  });

  const $inputParent = $el.parent();
  $inputParent.after($errorEl);
}

/**
 * Given a value map, load values into form where form field matches key.
 *
 */
export function loadValuesIntoForm<
  FormValues extends Record<string, string> = Record<string, string>
>(
  form: MaybeJQueryHtmlElement<HTMLFormElement>,
  values: Partial<FormValues> = {},
  // used if the given value is not found in the select options
  selectOptionFallbackValues: Partial<FormValues> = {}
) {
  // load form element into jQuery
  const $form = ensureJQueryObject(form);

  Object.entries(values).forEach(([key, value]) => {
    const $target = $form.find(`input[name="${key}"]`);

    if (!$target[0]) return;

    // Special handling for radios
    if ($target.first().prop("type") === "radio") {
      $target.filter(`[value="${value}"]`).attr("checked", "checked");
      return;
    }

    const $targetTagName: HTMLElement["tagName"] = $target.prop("tagName");

    switch ($targetTagName) {
      case "INPUT":
      case "TEXTAREA":
        $target.val(value);
        break;
      case "SELECT":
        setSelectValue(
          $target as JQuery<HTMLSelectElement>,
          value,
          selectOptionFallbackValues[key]
        );
        break;
      default:
        console.info(`Unsupported input type: ${$targetTagName}`);
        break;
    }
  });
}

export function showErrorsForForm(
  form: MaybeJQueryHtmlElement<HTMLFormElement>,
  errors: Record<string, string> = {}
) {
  // load form element into jQuery
  const $form = ensureJQueryObject(form);

  Object.entries(errors).forEach(([key, value]) => {
    const $target = $form.find(`input[name="${key}"]`);

    if (!$target[0]) return;

    addErrorTextAfterElement($target, value);
  });
}

export function markRequiredInputs(inputs: MaybeJQueryHtmlElement<HTMLElement>) {
  const $requiredFields = ensureJQueryObject(inputs);

  $requiredFields.each((_, field) => {
    const $fieldLabel = $(`label[for=${field.id}]`);

    const $symbolTag = $("<span>*</span>");
    $symbolTag.css({ "margin-left": "0.125rem" });
    $fieldLabel.append($symbolTag);
  });
}
